import React, { useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Tooltip,
  Chip,
} from "@mui/material";
import LabelIcon from "@mui/icons-material/Label";
import CategoryIcon from "@mui/icons-material/Category";
import { useTagTypes, useTagValues } from "../../../api/tags/tags";

function buildTagTypeTree(caseTags, valueMap, typeMap) {
  const byType = {};
  caseTags.forEach((tag) => {
    const val = valueMap[tag.value || tag.id];
    if (!val) return;
    const typeId = val.type || tag.type;
    if (!byType[typeId]) byType[typeId] = [];
    byType[typeId].push(val);
  });
  return Object.entries(byType).map(([typeId, tagValues]) => ({
    id: `type-${typeId}`,
    name: typeMap[typeId] || `Type ${typeId}`,
    isType: true,
    typeId,
    children: buildTagValueTree(tagValues, valueMap),
  }));
}

function buildTagValueTree(tagValues, valueMap) {
  const map = {};
  tagValues.forEach((val) => {
    map[val.id] = { ...val, isType: false, children: [] };
  });
  const roots = [];
  tagValues.forEach((val) => {
    if (val.parent && map[val.parent]) {
      map[val.parent].children.push(map[val.id]);
    } else if (map[val.id]) {
      roots.push(map[val.id]);
    }
  });
  return roots;
}

function TagsTreeView({ nodes, selectedIds, level = 0 }) {
  if (!nodes || !nodes.length) return null;
  return (
    <Box ml={level * 3}>
      {nodes.map((node) => {
        if (node.isType) {
          return (
            <Box key={node.id} mb={1}>
              <Chip
                icon={<CategoryIcon sx={{ color: "#1976d2" }} />}
                label={node.name}
                color="info"
                sx={{
                  fontWeight: 700,
                  background: "#e3f2fd",
                  color: "#1976d2",
                  mb: 1,
                  pl: 1,
                }}
              />
              {node.children && node.children.length > 0 && (
                <TagsTreeView
                  nodes={node.children}
                  selectedIds={selectedIds}
                  level={level + 1}
                />
              )}
            </Box>
          );
        }
        const isMatched = selectedIds.has(node.id);
        return (
          <Box key={node.id} mb={0.5}>
            <Tooltip
              title={
                <Box>
                  <Typography fontWeight={700}>{node.name}</Typography>
                  <Typography variant="body2">ID: {node.id}</Typography>
                  {node.type && (
                    <Typography variant="body2">Type: {node.type}</Typography>
                  )}
                  {node.parent && (
                    <Typography variant="body2">
                      Parent: {node.parent}
                    </Typography>
                  )}
                </Box>
              }
              arrow
            >
              <Chip
                icon={<LabelIcon sx={{ color: "#388e3c" }} />}
                label={node.name}
                color={isMatched ? "success" : "default"}
                variant={isMatched ? "filled" : "outlined"}
                sx={{
                  fontWeight: isMatched ? 700 : 400,
                  border: isMatched ? "2px solid #00e676" : "1px solid #bdbdbd",
                  background: isMatched ? "#b9f6ca" : "#f4f4f4",
                  color: isMatched ? "#1b5e20" : undefined,
                  opacity: isMatched ? 1 : 0.85,
                  ml: 2,
                }}
              />
            </Tooltip>
            {node.children?.length > 0 && (
              <TagsTreeView
                nodes={node.children}
                selectedIds={selectedIds}
                level={level + 1}
              />
            )}
          </Box>
        );
      })}
    </Box>
  );
}

export function CaseTagsDialog({
  open,
  onClose,
  caseTags = [],
  selectedTags = [],
  tagValues = [],
  tagTypes = [],
}) {
  const { data: types = [] } = useTagTypes();
  const { data: values = [] } = useTagValues();

  const typeMap = useMemo(() => {
    const m = {};
    (tagTypes.length ? tagTypes : types).forEach((t) => (m[t.id] = t.name));
    return m;
  }, [types, tagTypes]);

  const valueMap = useMemo(() => {
    const arr = tagValues.length ? tagValues : values;
    const m = {};
    arr.forEach((tv) => (m[tv.id] = tv));
    return m;
  }, [tagValues, values]);

  const selectedIds = useMemo(
    () => new Set((selectedTags || []).map((t) => t.id || t)),
    [selectedTags]
  );

  const tagTree = useMemo(
    () => buildTagTypeTree(caseTags, valueMap, typeMap),
    [caseTags, valueMap, typeMap]
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Case Tags</DialogTitle>
      <DialogContent>
        {tagTree.length ? (
          <TagsTreeView nodes={tagTree} selectedIds={selectedIds} />
        ) : (
          <Typography color="textSecondary">No tags</Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary" variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
